import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useOutletContext } from 'react-router-dom'
import { useEffect, useRef } from 'react'

import { useAuth } from '@App/components/AuthProvider/AuthProvider'
import { fetchMissionDataTypesByGroup, fetchMissionDataByProps } from '@services/data.service'
import createListPopup from '@App/components/Map/popups/ListPopup/ListPopup'

import './environment-data.styl'

function EnvironmentData({ passive = false }) {
  const { map } = useOutletContext()
  const popupRef = useRef(null)
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const {
    activeProject,
    sourcesActiveNames,
    environmentActiveTypeIds,
  } = useSelector(state => state.missionSetup)

  const { data: types } = useQuery({
    queryKey: ['types', 'env', activeProject?.id],
    queryFn: () => fetchMissionDataTypesByGroup('env', activeProject?.id),
  })

  const { data } = useQuery({
    queryKey: [
      'env', activeProject?.id, sourcesActiveNames, environmentActiveTypeIds,
    ],
    queryFn: () => fetchMissionDataByProps(
      activeProject?.id, 'env', sourcesActiveNames, environmentActiveTypeIds,
    ),
    enabled: !!activeProject?.id && !!sourcesActiveNames && !!environmentActiveTypeIds,
  })

  useEffect(() => {
    const canvasContainer = document.querySelector('.maplibregl-canvas')

    const layers = ['environment-layer', 'environment-polygon-layer', 'environment-line-layer']

    const handleMouseEnter = () => {
      if (canvasContainer) canvasContainer.classList.add('pointer-cursor')
    }

    const handleMouseLeave = () => {
      if (canvasContainer) canvasContainer.classList.remove('pointer-cursor')
    }

    const handleClick = (event) => {
      const feature = event?.features?.[0]
      if (!feature) return

      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }

      popupRef.current = createListPopup({
        map,
        coordinates: event.lngLat,
        props: feature.properties,
        queryClient,
        userId: user?.sub,
      })
    }

    if (data && map && sourcesActiveNames) {
      map.getSource('environment')?.setData({
        type: 'FeatureCollection',
        features: (data ?? []).map((envData) => {
          const { geometry, properties = {} } = envData
          const dataTypeId = properties.dataType
          const type = types?.find(type => type.id === dataTypeId)

          return {
            type: 'Feature',
            properties: {
              icon: type?.icon || 'question',
              color: type?.color || '#000000',
              ...properties,
            },
            geometry,
          }
        }),
      })

      if (!passive) {
        map.on('mouseenter', layers, handleMouseEnter)
        map.on('mouseleave', layers, handleMouseLeave)
        map.on('click', layers, handleClick)
      } else {
        const setPassiveStyles = () => {
          if (map.getLayer('environment-layer')) {
            map.setPaintProperty('environment-layer', 'icon-opacity', 0.6)
            map.setLayoutProperty('environment-layer', 'icon-allow-overlap', false)
          }
        }

        if (map.isStyleLoaded()) {
          setPassiveStyles()
        } else {
          map.once('idle', setPassiveStyles)
        }
      }
    }

    return () => {
      if (canvasContainer) canvasContainer.classList.remove('pointer-cursor')

      if (map) {
        map.off('mouseenter', layers, handleMouseEnter)
        map.off('mouseleave', layers, handleMouseLeave)
        map.off('click', layers, handleClick)
      }

      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }

      if (map?.getSource('environment')) {
        map.getSource('environment')?.setData({
          type: 'FeatureCollection',
          features: [],
        })
      }

      if (map?.getLayer('environment-layer')) {
        map.setPaintProperty('environment-layer', 'icon-opacity', 1)
        map.setLayoutProperty('environment-layer', 'icon-allow-overlap', true)
      }
    }
  }, [map, data, types, sourcesActiveNames, queryClient, passive, user?.sub])

  return null
}

export default EnvironmentData
